import React, { useEffect, useState } from 'react';
import useAppStore from '../../store/appStore';
import './LLMSettings.css';

const PROVIDER_META = {
  'gpt-oss': {
    icon: '⚡',
    badge: 'Remote',
  },
  ollama: {
    icon: '🖥️',
    badge: 'Local',
  },
};

export default function LLMSettings() {
  const llmConfig = useAppStore(s => s.llmConfig);
  const llmSaving = useAppStore(s => s.llmSaving);
  const loadLLMConfig = useAppStore(s => s.loadLLMConfig);
  const updateLLMConfig = useAppStore(s => s.updateLLMConfig);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadLLMConfig().catch(() => {});
  }, [loadLLMConfig]);

  const handleSetProvider = async (providerId) => {
    if (!llmConfig) return;
    try {
      await updateLLMConfig(providerId, llmConfig.llm_enabled);
      setFeedback({ type: 'success', msg: `Switched to ${providerId}` });
    } catch {
      setFeedback({ type: 'error', msg: 'Failed to switch provider' });
    }
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleToggleEnabled = async () => {
    if (!llmConfig) return;
    try {
      await updateLLMConfig(llmConfig.active_provider, !llmConfig.llm_enabled);
      setFeedback({ type: 'success', msg: llmConfig.llm_enabled ? 'LLM disabled — using rule-based fallback' : 'LLM enabled' });
    } catch {
      setFeedback({ type: 'error', msg: 'Failed to update setting' });
    }
    setTimeout(() => setFeedback(null), 3000);
  };

  if (!llmConfig) {
    return (
      <div className="llm-settings">
        <div className="llm-loading">Loading LLM configuration…</div>
      </div>
    );
  }

  const providers = llmConfig.providers || {};
  const active = llmConfig.active_provider;
  const enabled = llmConfig.llm_enabled;

  return (
    <div className="llm-settings">
      <div className="llm-header">
        <h2 className="llm-title">Language Model</h2>
        <p className="llm-subtitle">
          Controls which LLM is used to parse your messages and generate responses.
        </p>
      </div>

      {feedback && (
        <div className={`llm-feedback llm-feedback--${feedback.type}`}>{feedback.msg}</div>
      )}

      <div className="llm-toggle-row">
        <div className="llm-toggle-info">
          <span className="llm-toggle-label">LLM pipeline</span>
          <span className="llm-toggle-desc">
            {enabled ? 'Active — full natural language understanding' : 'Off — using rule-based fallback (fast, limited)'}
          </span>
        </div>
        <button
          className={`llm-toggle-btn ${enabled ? 'llm-toggle-btn--on' : 'llm-toggle-btn--off'}`}
          onClick={handleToggleEnabled}
          disabled={llmSaving}
        >
          {enabled ? 'ON' : 'OFF'}
        </button>
      </div>

      <div className="llm-providers">
        {Object.entries(providers).map(([id, info]) => {
          const meta = PROVIDER_META[id] || { icon: '🤖', badge: '' };
          const isActive = id === active;
          return (
            <div key={id} className={`llm-card ${isActive ? 'llm-card--active' : ''}`}>
              <div className="llm-card-header">
                <span className="llm-card-icon">{meta.icon}</span>
                <div className="llm-card-title-group">
                  <span className="llm-card-name">{info.label}</span>
                  <span className="llm-card-badge">{meta.badge}</span>
                </div>
                <span className={`llm-online-dot ${info.online ? 'online' : 'offline'}`}
                  title={info.online ? 'Online' : 'Offline'} />
              </div>

              <p className="llm-card-desc">{info.description}</p>

              <div className="llm-card-meta">
                <div className="llm-meta-row">
                  <span className="llm-meta-key">Model</span>
                  <span className="llm-meta-val">{info.model}</span>
                </div>
                <div className="llm-meta-row">
                  <span className="llm-meta-key">Endpoint</span>
                  <span className="llm-meta-val llm-meta-url">{info.base_url}</span>
                </div>
                <div className="llm-meta-row">
                  <span className="llm-meta-key">Status</span>
                  <span className={`llm-status-text ${info.online ? 'online' : 'offline'}`}>
                    {info.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              {isActive ? (
                <div className="llm-active-badge">Active provider</div>
              ) : (
                <button
                  className="llm-set-btn"
                  onClick={() => handleSetProvider(id)}
                  disabled={llmSaving || !info.online}
                  title={!info.online ? 'Provider is offline' : ''}
                >
                  {llmSaving ? 'Switching…' : 'Set Active'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="llm-note">
        Switching providers takes effect immediately for all new messages.
        If the active provider goes offline, rule-based parsing is used automatically.
      </div>
    </div>
  );
}
